import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Paperclip, Send, AlertTriangle, Loader2 } from "lucide-react";
import { formatFileSize } from "@/lib/api";

/**
 * Dialog riutilizzabile che mostra l'anteprima di un'email PRIMA di inviarla.
 * Chiama `fetchPreview()` all'apertura per ottenere { to, subject, html_body, attachments[], meta }.
 * Se l'utente conferma, chiama `onConfirmSend()`.
 *
 * `emailTypeKey`: chiave univoca per memorizzare l'opzione "Non chiedermelo più".
 * Il ripristino globale si fa da user menu → "Ripristina anteprime email".
 */
const SKIP_LS_KEY = "emailPreviewSkip";

export function shouldSkipEmailPreview(emailTypeKey) {
  if (!emailTypeKey) return false;
  try {
    const arr = JSON.parse(localStorage.getItem(SKIP_LS_KEY) || "[]");
    return Array.isArray(arr) && arr.includes(emailTypeKey);
  } catch { return false; }
}

export function addSkipEmailPreview(emailTypeKey) {
  try {
    const arr = JSON.parse(localStorage.getItem(SKIP_LS_KEY) || "[]");
    const set = new Set(Array.isArray(arr) ? arr : []);
    set.add(emailTypeKey);
    localStorage.setItem(SKIP_LS_KEY, JSON.stringify(Array.from(set)));
  } catch { /* ignore */ }
}

export function resetEmailPreviewSkips() {
  try { localStorage.removeItem(SKIP_LS_KEY); return true; } catch { return false; }
}

export function EmailPreviewDialog({ open, onOpenChange, emailTypeKey, fetchPreview, onConfirmSend, title = "Anteprima email prima dell'invio", readOnly = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null); // { to, subject, html_body, attachments, meta }
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [sending, setSending] = useState(false);
  // 🆕 Feb 2026 — Lock sincrono anti-doppio-click: setState è asincrono quindi un doppio-click rapido
  // (o un trigger multiplo dell'onClick) può bypassare `disabled={sending}`. useRef è sincrono.
  const sendingLockRef = useRef(false);

  useEffect(() => {
    if (!open) { setPreview(null); setError(null); setDontAskAgain(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const p = await fetchPreview();
        if (!cancelled) setPreview(p);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Errore nel caricamento anteprima");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const doSend = async () => {
    if (sendingLockRef.current) return;  // 🆕 blocca doppio-click sincrono
    sendingLockRef.current = true;
    setSending(true);
    try {
      if (dontAskAgain && emailTypeKey) addSkipEmailPreview(emailTypeKey);
      await onConfirmSend();
      onOpenChange(false);
    } catch { /* errore già gestito dal chiamante */ }
    finally {
      setSending(false);
      sendingLockRef.current = false;
    }
  };

  const recipients = Array.isArray(preview?.to) ? preview.to : (preview?.to ? [preview.to] : []);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !sending) onOpenChange(false); }}>
      <DialogContent className="max-w-4xl" data-testid="email-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-800">
            <Mail className="h-5 w-5" /> {title}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-slate-500 text-sm flex flex-col items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin" />
            Caricamento anteprima…
          </div>
        ) : error ? (
          <div className="rounded-md bg-rose-50 border border-rose-300 p-3 text-sm text-rose-800 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Impossibile generare l&apos;anteprima</p>
              <p className="text-xs mt-0.5">{String(error)}</p>
            </div>
          </div>
        ) : preview ? (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {/* Destinatari */}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs">
              <div className="flex items-start gap-2 flex-wrap">
                <span className="font-semibold text-slate-600 uppercase tracking-wide">A:</span>
                {recipients.length > 0 ? recipients.map((r, i) => (
                  <span key={i} className="font-mono bg-white border border-slate-300 rounded px-1.5 py-0.5">{r}</span>
                )) : <span className="italic text-slate-400">Nessun destinatario</span>}
              </div>
              {preview.meta?.client_name && (
                <div className="mt-1 text-slate-600">
                  <span className="font-semibold uppercase tracking-wide">Cliente:</span> {preview.meta.client_name}
                </div>
              )}
            </div>

            {/* Oggetto */}
            <div className="rounded-md border border-blue-200 bg-blue-50 p-2.5 text-sm">
              <span className="text-[10px] uppercase tracking-wide text-blue-600 font-semibold">Oggetto</span>
              <p className="font-semibold text-blue-900 mt-0.5 flex items-center gap-1.5 flex-wrap" data-testid="email-preview-subject">
                <span>{preview.subject || "—"}</span>
                {preview.meta?.is_installment_expiry && (
                  <span
                    data-testid="email-preview-rata-badge"
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 border border-indigo-300 uppercase tracking-wide"
                    title={`Prossima rata da pagare: ${preview.meta?.effective_end_date || ""} (fine polizza: ${preview.meta?.end_date || ""})`}
                  >
                    Rata
                  </span>
                )}
              </p>
            </div>

            {/* 🆕 22/07/2026 — Mini-riepilogo scadenze per email di scadenza polizza/rata */}
            {(preview.meta?.effective_end_date || preview.meta?.end_date) && (
              <div className="rounded-md border border-slate-200 bg-white p-2.5 text-[11px] flex flex-wrap gap-4 tabular-nums" data-testid="email-preview-expiry-summary">
                {preview.meta?.effective_end_date && (
                  <div>
                    <span className="uppercase text-[9px] tracking-wider font-semibold text-indigo-700">Prossima scadenza</span>
                    <div className="text-slate-900 font-semibold">{preview.meta.effective_end_date.split("-").reverse().join("/")}</div>
                  </div>
                )}
                {typeof preview.meta?.days_left === "number" && (
                  <div>
                    <span className="uppercase text-[9px] tracking-wider font-semibold text-slate-500">Restanti</span>
                    <div className={preview.meta.days_left < 0 ? "text-rose-600 font-semibold" : preview.meta.days_left <= 7 ? "text-amber-700 font-semibold" : "text-slate-900 font-semibold"}>
                      {preview.meta.days_left < 0 ? `Scaduta ${Math.abs(preview.meta.days_left)}gg fa` : preview.meta.days_left === 0 ? "Oggi" : `${preview.meta.days_left} giorni`}
                    </div>
                  </div>
                )}
                {preview.meta?.end_date && preview.meta?.is_installment_expiry && (
                  <div>
                    <span className="uppercase text-[9px] tracking-wider font-semibold text-slate-500">Fine polizza</span>
                    <div className="text-slate-500">{preview.meta.end_date.split("-").reverse().join("/")}</div>
                  </div>
                )}
              </div>
            )}

            {/* 🆕 Feb 2026 — Blocco veicolo (marca/modello/targa) — mostrato solo per polizze auto */}
            {(preview.meta?.vehicle_brand || preview.meta?.vehicle_model || preview.meta?.vehicle_plate) && (
              <div
                className="rounded-md border border-blue-300 bg-blue-50 p-2.5 text-xs flex flex-wrap gap-4 items-center"
                data-testid="email-preview-vehicle-block"
              >
                <span className="uppercase text-[10px] tracking-wider font-bold text-blue-700">🚗 Veicolo assicurato</span>
                {(preview.meta?.vehicle_brand || preview.meta?.vehicle_model) && (
                  <div>
                    <span className="uppercase text-[9px] tracking-wider font-semibold text-blue-700">Marca e modello</span>
                    <div className="text-slate-900 font-semibold" data-testid="email-preview-vehicle-brand-model">
                      {[preview.meta?.vehicle_brand, preview.meta?.vehicle_model].filter(Boolean).join(" ")}
                    </div>
                  </div>
                )}
                {preview.meta?.vehicle_plate && (
                  <div>
                    <span className="uppercase text-[9px] tracking-wider font-semibold text-blue-700">Targa</span>
                    <div className="text-slate-900 font-bold font-mono tracking-wider" data-testid="email-preview-vehicle-plate">
                      {preview.meta.vehicle_plate}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Allegati */}
            {(preview.attachments || []).length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5">
                <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold flex items-center gap-1">
                  <Paperclip className="h-3 w-3" /> {preview.attachments.length} allegat{preview.attachments.length === 1 ? "o" : "i"}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {preview.attachments.map((a, i) => (
                    <li key={i} className="text-xs text-amber-900 flex items-center gap-1.5">
                      <span>📎</span>
                      <span className="font-mono truncate">{a.filename || "file"}</span>
                      {a.size ? <span className="text-amber-600 text-[10px]">({formatFileSize(a.size)})</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Preview HTML live */}
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Anteprima corpo email (rendering)</p>
              <div className="border border-slate-300 rounded-md bg-white shadow-inner">
                <iframe
                  data-testid="email-preview-iframe"
                  srcDoc={preview.html_body || "<em>Nessun contenuto</em>"}
                  title="Email preview"
                  sandbox="allow-same-origin"
                  style={{ width: "100%", height: "50vh", border: 0, borderRadius: 6 }}
                />
              </div>
            </div>
          </div>
        ) : null}

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-between items-stretch sm:items-center">
          {!readOnly && (
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={dontAskAgain}
                onChange={(e) => setDontAskAgain(e.target.checked)}
                className="h-3.5 w-3.5"
                data-testid="email-preview-skip-checkbox"
              />
              Non chiedermelo più per questo tipo di email
              <span className="text-[10px] text-slate-400">(ripristinabile dalle impostazioni)</span>
            </label>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending} data-testid="email-preview-cancel">
              {readOnly ? "Chiudi" : "Annulla"}
            </Button>
            {!readOnly && (
              <Button
                onClick={doSend}
                disabled={loading || !!error || sending}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="email-preview-send"
              >
                {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
                Invia ora
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EmailPreviewDialog;
